/**
 * Round 218A Phase 8D-2 (2026-02-05) — Business Credits Settings
 * section.
 *
 * Renders inside SettingsPage → Billing tab. Shows:
 *   • Total Business Credits
 *   • Monthly credit balance
 *   • Purchased credit balance
 *   • Monthly allowance
 *   • Next expected refill date (derived from `refilled_at` + 30d)
 *   • Rollover / grace policy copy
 *   • "Add credits" button that opens the Insufficient Credits modal
 *
 * Never exposes ledger internals, per-asset charges, or provider
 * costs. Founder / internal accounts get an Unlimited card without
 * numeric balances.
 *
 * On mount, if the URL carries `?credit_purchase=success`, we
 * refresh the balance and show a success toast. `credit_purchase=
 * cancelled` shows a soft info toast. Both query params are cleaned
 * up so a browser reload doesn't retrigger.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Coins, CalendarCheck, Wallet, Sparkle } from '@phosphor-icons/react';
import useBusinessCredits, { notifyBusinessCreditsChanged } from '@/hooks/useBusinessCredits';
import InsufficientCreditsModal from './InsufficientCreditsModal';

const _formatDate = (isoString) => {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return '—';
  }
};

const _computeNextRefill = (refilledAt) => {
  if (!refilledAt) return null;
  try {
    const last = new Date(refilledAt);
    if (Number.isNaN(last.getTime())) return null;
    const next = new Date(last);
    next.setDate(next.getDate() + 30);
    return next.toISOString();
  } catch {
    return null;
  }
};

const _cleanCheckoutQueryParams = () => {
  try {
    const url = new URL(window.location.href);
    if (
      url.searchParams.has('credit_purchase')
      || url.searchParams.has('session_id')
    ) {
      url.searchParams.delete('credit_purchase');
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.toString());
    }
  } catch {
    /* no-op */
  }
};

const BusinessCreditsSettingsSection = () => {
  const { loading, error, balance, refresh } = useBusinessCredits();
  const [modalOpen, setModalOpen] = useState(false);

  // Handle Stripe checkout return.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const outcome = params.get('credit_purchase');
      if (!outcome) return;
      if (outcome === 'success') {
        toast.success('Business Credits added.');
        refresh();
        notifyBusinessCreditsChanged();
      } else if (outcome === 'cancelled') {
        toast.message('Purchase cancelled. Your project is still saved.');
      }
      _cleanCheckoutQueryParams();
    } catch {
      /* no-op */
    }
  }, []);

  const isUnlimited = Boolean(balance?.is_unlimited);
  const total = balance?.total ?? 0;
  const monthly = balance?.monthly ?? 0;
  const purchased = balance?.purchased ?? 0;
  const monthlyQuota = balance?.monthly_quota ?? 30;
  const nextRefillIso = _computeNextRefill(balance?.refilled_at);
  const policy = balance?.policy || {};

  if (loading && !balance) {
    return (
      <div
        className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5"
        data-testid="business-credits-settings-loading"
      >
        <div className="h-4 w-32 rounded bg-slate-800 animate-pulse" />
        <div className="mt-3 h-8 w-48 rounded bg-slate-800 animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 text-sm text-slate-400"
        data-testid="business-credits-settings-error"
      >
        Business Credits balance is temporarily unavailable. Please refresh
        the page in a moment.
      </div>
    );
  }

  return (
    <>
      <section
        className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5 space-y-5"
        data-testid="business-credits-settings"
      >
        {/* Heading */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-slate-300">
              <Coins size={16} weight="fill" className="text-emerald-400" />
              <h3 className="text-sm font-semibold uppercase tracking-wide">
                Business Credits
              </h3>
            </div>
            <p className="mt-1 text-xs text-slate-400">
              Credits pay for professional production. Your subscription pays
              for the strategist.
            </p>
          </div>
          {!isUnlimited && (
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-slate-950 hover:bg-emerald-400 transition-colors"
              data-testid="business-credits-settings-add-btn"
            >
              Add credits
            </button>
          )}
        </div>

        {/* Balance row */}
        {isUnlimited ? (
          <div
            className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4"
            data-testid="business-credits-unlimited-card"
          >
            <div className="flex items-center gap-2 text-emerald-300">
              <Sparkle size={16} weight="fill" />
              <span className="text-sm font-semibold">Unlimited</span>
            </div>
            <p className="mt-1 text-xs text-emerald-200/80">
              Founder / internal accounts don&rsquo;t consume Business Credits.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div
              className="rounded-xl border border-slate-800 bg-slate-950/40 p-3"
              data-testid="business-credits-total-card"
            >
              <div className="text-[11px] uppercase tracking-wide text-slate-400">
                Total
              </div>
              <div className="mt-1 text-2xl font-bold text-white tabular-nums">
                {total.toLocaleString('en-US')}
              </div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">
                Monthly
              </div>
              <div
                className="mt-1 text-2xl font-semibold text-slate-100 tabular-nums"
                data-testid="business-credits-monthly-balance"
              >
                {monthly.toLocaleString('en-US')}
              </div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">
                Purchased
              </div>
              <div
                className="mt-1 text-2xl font-semibold text-slate-100 tabular-nums"
                data-testid="business-credits-purchased-balance"
              >
                {purchased.toLocaleString('en-US')}
              </div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
              <div className="text-[11px] uppercase tracking-wide text-slate-400">
                Monthly allowance
              </div>
              <div className="mt-1 text-2xl font-semibold text-slate-100 tabular-nums">
                {monthlyQuota.toLocaleString('en-US')}
              </div>
            </div>
          </div>
        )}

        {/* Next refill + policy */}
        {!isUnlimited && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <div className="rounded-xl border border-slate-800 bg-slate-950/30 p-3">
              <div className="flex items-center gap-2 text-slate-400 text-xs">
                <CalendarCheck size={14} />
                <span>Next expected refill</span>
              </div>
              <div
                className="mt-1 text-sm font-medium text-slate-100"
                data-testid="business-credits-next-refill"
              >
                {_formatDate(nextRefillIso)}
              </div>
            </div>
            <div className="rounded-xl border border-slate-800 bg-slate-950/30 p-3">
              <div className="flex items-center gap-2 text-slate-400 text-xs">
                <Wallet size={14} />
                <span>Rollover & grace policy</span>
              </div>
              <ul className="mt-1 space-y-1 text-xs text-slate-300 list-disc list-inside">
                <li>{policy.rollover_copy || 'Monthly credits roll over up to 2× your monthly allowance.'}</li>
                <li>{policy.purchased_copy || 'Purchased credits remain available while your subscription is active.'}</li>
                <li>{policy.grace_copy || 'After cancellation, purchased credits have a 90-day reactivation grace period.'}</li>
              </ul>
            </div>
          </div>
        )}
      </section>

      <InsufficientCreditsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Add Business Credits"
        message="Top up your Business Credits so professional production is ready when you need it."
      />
    </>
  );
};

export default BusinessCreditsSettingsSection;
